import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { INIT_MESSAGE_STATE, InitMessage } from '../../services/base-initialization/init-message';
import { ReportIssueComponent } from '../report-issue/report-issue.component';
import { ReportIssueModel } from '../report-issue/report-issue.model';
import { map } from 'rxjs/operators';
import { LocalStorageCommonService } from '../../services/local-storage/local-storage-common.service';
import { ConfigConstants } from '../../config/config-constants';

@Component({
    selector: 'pace-core-initialization-progress',
    templateUrl: './initialization-progress.component.html',
    styleUrls: ['./initialization-progress.component.scss'],
})
export class InitializationProgressComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public progress$: Observable<InitMessage[]>;

    public progressInfo$: Observable<ProgressInfo>;
    public rendered$: Observable<boolean>;
    public applicationName: string;
    public isErrorCollapsed: boolean;
    public currentAppVersion: string;
    public currentBackend: string;

    private rendered: BehaviorSubject<boolean>;
    public constructor(
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly localStorage: LocalStorageCommonService,
        private readonly translate: TranslateService,
    ) {
        this.rendered = new BehaviorSubject<boolean>(false);
        this.rendered$ = this.rendered.asObservable();

        this.applicationName = this.clientManager.getBundleIdentifier();
    }

    public ngOnInit(): void {
        this.isErrorCollapsed = true;
        this.currentAppVersion = this.clientManager.getAppVersion();
        this.currentBackend =
            this.localStorage.preInitializationStorageGet(ConfigConstants.configKeyBackendName) ??
            this.translate.instant('APP_START_LOADING_BACKEND');
        this.progressInfo$ = this.progress$.pipe(
            map((progress) => ({
                progress,
                isInitError: progress.reduce((acc, msg) => acc || msg.state === INIT_MESSAGE_STATE.ERROR, false),
                currentMessage:
                    progress.find((m) => m.state === INIT_MESSAGE_STATE.PENDING) ??
                    progress.findLast(
                        (m) => m.state === INIT_MESSAGE_STATE.SUCCESS || m.state === INIT_MESSAGE_STATE.WARNING,
                    ),
                errorList: progress.filter((message) => message.state === INIT_MESSAGE_STATE.ERROR),
            })),
        );
    }

    public ngAfterViewInit(): void {
        this.rendered.next(true);
    }

    public ngOnDestroy(): void {
        this.rendered.complete();
    }

    public openReportIssue(): void {
        this.modalService.show(ReportIssueComponent, {
            backdrop: 'static',
            class: 'modal-lg modal-fullscreen-lg-down',
            initialState: {
                model: new ReportIssueModel(),
            },
            ariaLabelledBy: 'report-issue-modal-title',
        });
    }

    public tryAgain(): void {
        window.location.reload();
    }

    public toggleErrors(): void {
        this.isErrorCollapsed = !this.isErrorCollapsed;
    }
}

interface ProgressInfo {
    progress: Array<Partial<InitMessage>>;
    isInitError: boolean;
    currentMessage?: InitMessage;
    errorList: Array<Partial<InitMessage>>;
}
